import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import {
  // createBreadcrumb,
  createBreadcrumbWithI18n,
  createLabeledPathWithI18n,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import { HStack } from '~/components/elements/HStack'
import { Icon } from '~/components/elements/Icon'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { assertExists } from '~/utils'

import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useDocumentReportId } from '../hooks/useDocumentReportId'
import { useMainQueryData } from '../hooks/useMainQuery'
import { createResolver, type InputValues } from '../utils'

import { DocumentReportArchiveButton } from './DocumentReportArchiveButton'
import { DocumentReportCloneButton } from './DocumentReportCloneButton'
import { DocumentReportDownloadButton } from './DocumentReportDownloadButton'
import styles from './DocumentReportEdit.css'
import { DocumentReportEditEditor } from './DocumentReportEditEditor'
import { DocumentReportEditPrompt } from './DocumentReportEditPrompt'
import { DocumentReportEditTitleInput } from './DocumentReportEditTitleInput'
import { DocumentReportSaveButton } from './DocumentReportSaveButton'
import { DocumentReportShareButton } from './DocumentReportShareButton'

export const DocumentReportEdit = () => {
  const { t } = useTranslation()
  const createLabeledPath = React.useMemo(
    () => createLabeledPathWithI18n(t),
    [t],
  )
  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])

  const documentReport = useMainQueryData()
  // TODO : 404にする
  assertExists(documentReport, t('assertion_failed'))
  const methods = useForm<InputValues>({
    defaultValues: {
      title: documentReport.title,
      content: documentReport.content,
    },
    resolver: createResolver(t),
  })
  const { watch } = methods
  const title = watch('title')

  const brandAccountId = useBrandAccountId()
  const documentReportId = useDocumentReportId()
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('documentManagement'),
      createBreadcrumb('documentReportList', { brandAccountId }),
      createBreadcrumb(
        'documentReportEdit',
        { brandAccountId, documentReportId },
        title,
      ),
    ],
    [brandAccountId, documentReportId, title, createBreadcrumb],
  )

  const labeledPath = React.useMemo(
    () => createLabeledPath('documentReportList', { brandAccountId }),
    [brandAccountId, createLabeledPath],
  )

  return (
    <FormProvider {...methods}>
      <PageContainer breadcrumbs={breadcrumbs}>
        <VStack spacing={2} verticalAppearance="stretch-scroll">
          <HStack align="center" justify="space-between" spacing={4}>
            <Link to={labeledPath.to}>
              <Typography variant="normal">
                <Icon icon="arrow-chevron-left" size="1em" />

                {t('back_to_labeledpath_label', { labeledPath })}
              </Typography>
            </Link>

            <HStack spacing={1}>
              <DocumentReportSaveButton />

              <DocumentReportShareButton />

              <DocumentReportDownloadButton />

              <DocumentReportCloneButton />

              <DocumentReportArchiveButton />
            </HStack>
          </HStack>

          <DocumentReportEditTitleInput />

          <DocumentReportEditEditor className={styles.editor} />

          <HStack
            align="center"
            className={styles.footer}
            justify="start"
            spacing={4}
          >
            <Typography variant="normal">
              {t('last_saved')}
              {new Date(documentReport.updatedAt).toLocaleString()}
            </Typography>
          </HStack>
        </VStack>

        <DocumentReportEditPrompt />
      </PageContainer>
    </FormProvider>
  )
}
